import React, { useState, useEffect } from 'react';
import Snackbar from './Snackbar';
import { getApiBaseUrl } from '../config/api';
import './CommissionsPayable.css';
import '../styles/analytics-theme.css';

const CommissionsPayable = ({ user, onShowNotification }) => {
  const [commissions, setCommissions] = useState([]);
  const [totals, setTotals] = useState({
    total_premium: 0,
    total_commission: 0,
    total_tax: 0,
    total_net_commission: 0,
    total_pending: 0,
    total_paid: 0,
    total_overdue: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    agentId: '',
    startDate: '',
    endDate: '',
    paymentStatus: 'all',
    dateRange: '30d'
  });
  const [agents, setAgents] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [paymentData, setPaymentData] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('info');
  const API_BASE_URL = getApiBaseUrl();

  const showNotification = (message, type = 'info') => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setShowSnackbar(true);
    if (onShowNotification) {
      onShowNotification(message, type);
    }
  };

  const getAuthToken = () => {
    return localStorage.getItem('adminToken');
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    fetchCommissions();
  }, [filters, pagination.page]);

  useEffect(() => {
    // Update date range when dateRange filter changes
    if (filters.dateRange && filters.dateRange !== 'custom') {
      const endDate = new Date();
      const startDate = new Date();
      
      switch (filters.dateRange) {
        case '7d':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(endDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(endDate.getDate() - 90);
          break;
        case 'month':
          startDate.setDate(1);
          break;
        case 'quarter':
          const quarter = Math.floor(endDate.getMonth() / 3);
          startDate.setMonth(quarter * 3);
          startDate.setDate(1);
          break;
        case 'year':
          startDate.setMonth(0);
          startDate.setDate(1);
          break;
        default:
          break;
      }
      
      setFilters({
        ...filters,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      });
    }
  }, [filters.dateRange]);

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/agents?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setAgents(data.agents || []);
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  const fetchCommissions = async () => {
    setIsLoading(true);

    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });

      if (filters.agentId) {
        params.append('agentId', filters.agentId);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }
      if (filters.paymentStatus !== 'all') {
        params.append('paymentStatus', filters.paymentStatus);
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/commissions?${params}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setCommissions(data.commissions || []);
        setTotals(data.totals || totals);
        setPagination(data.pagination || pagination);
      } else {
        showNotification(data.error || 'Failed to fetch commissions', 'error');
      }
    } catch (err) {
      console.error('Error fetching commissions:', err);
      showNotification('Failed to fetch commissions', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsPaid = async (commissionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/commissions/${commissionId}/pay`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({
          paymentDate: paymentData.paymentDate || new Date().toISOString().split('T')[0],
          notes: paymentData.notes
        })
      });

      const data = await response.json();
      if (data.success) {
        showNotification('Commission marked as paid successfully', 'success');
        setShowPaymentModal(null);
        setPaymentData({ paymentDate: new Date().toISOString().split('T')[0], notes: '' });
        fetchCommissions();
      } else {
        showNotification(data.error || 'Failed to mark commission as paid', 'error');
      }
    } catch (err) {
      console.error('Error marking commission as paid:', err);
      showNotification('Failed to mark commission as paid', 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { class: 'status-pending', text: 'Pending', icon: '⏳' },
      paid: { class: 'status-paid', text: 'Paid', icon: '✅' },
      overdue: { class: 'status-overdue', text: 'Overdue', icon: '⚠️' }
    };
    const statusInfo = statusMap[status?.toLowerCase()] || { class: 'status-unknown', text: status || 'Unknown', icon: '❓' };
    return (
      <span className={`status-badge ${statusInfo.class}`}>
        {statusInfo.icon} {statusInfo.text}
      </span>
    );
  };

  const canMarkAsPaid = user?.role === 'super_admin' || user?.role === 'finance_admin';

  return (
    <div className="commissions-payable">
      <div className="commissions-header">
        <div>
          <h2>💰 Commissions Payable</h2>
          <p>Track and manage agent commission payments</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card total-payable">
          <div className="card-icon">💵</div>
          <div className="card-content">
            <h3>Total Payable</h3>
            <p className="card-value">{formatCurrency(totals.total_pending || 0)}</p>
            <small>Pending commissions</small>
          </div>
        </div>
        <div className="summary-card total-paid">
          <div className="card-icon">✅</div>
          <div className="card-content">
            <h3>Paid This Period</h3>
            <p className="card-value">{formatCurrency(totals.total_paid || 0)}</p>
            <small>Commissions paid</small>
          </div>
        </div>
        <div className="summary-card total-overdue">
          <div className="card-icon">⚠️</div>
          <div className="card-content">
            <h3>Overdue</h3>
            <p className="card-value">{formatCurrency(totals.total_overdue || 0)}</p>
            <small>Overdue commissions</small>
          </div>
        </div>
        <div className="summary-card total-net">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <h3>Total Net Commission</h3>
            <p className="card-value">{formatCurrency(totals.total_net_commission || 0)}</p>
            <small>All commissions</small>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({ ...filters, dateRange: e.target.value, startDate: '', endDate: '' })}
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          {filters.dateRange === 'custom' && (
            <>
              <div className="filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                />
              </div>
              <div className="filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                />
              </div>
            </>
          )}
          <div className="filter-group">
            <label>Agent</label>
            <select
              value={filters.agentId}
              onChange={(e) => {
                setFilters({ ...filters, agentId: e.target.value });
                setPagination({ ...pagination, page: 1 });
              }}
            >
              <option value="">All Agents</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.agency_name || agent.username}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Payment Status</label>
            <select
              value={filters.paymentStatus}
              onChange={(e) => {
                setFilters({ ...filters, paymentStatus: e.target.value });
                setPagination({ ...pagination, page: 1 });
              }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div className="filter-actions">
            <button
              onClick={() => {
                setFilters({
                  agentId: '',
                  startDate: '',
                  endDate: '',
                  paymentStatus: 'all',
                  dateRange: '30d'
                });
                setPagination({ ...pagination, page: 1 });
              }}
              className="btn-clear-filters"
            >
              🔄 Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Commissions Table */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Loading commissions...</p>
        </div>
      ) : commissions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>No commissions found</h3>
          <p>No commissions match your current filters.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="commissions-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Agent</th>
                  <th>Employee</th>
                  <th>Policy #</th>
                  <th>Premium</th>
                  <th>Commission</th>
                  <th>Tax</th>
                  <th>Net Commission</th>
                  <th>Status</th>
                  <th>Paid Date</th>
                  <th>Paid By</th>
                  {canMarkAsPaid && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {commissions.map((commission) => (
                  <tr key={commission.id}>
                    <td>{formatDate(commission.transaction_date)}</td>
                    <td>{commission.agency_name || 'N/A'}</td>
                    <td>{commission.user_full_name || commission.user_username || 'N/A'}</td>
                    <td>{commission.policy_number || 'N/A'}</td>
                    <td className="amount-cell">{formatCurrency(commission.premium_amount)}</td>
                    <td className="amount-cell">{formatCurrency(commission.commission_amount)}</td>
                    <td className="amount-cell">{formatCurrency(commission.withholding_tax_amount)}</td>
                    <td className="amount-cell net-commission">{formatCurrency(commission.net_commission)}</td>
                    <td>{getStatusBadge(commission.payment_status)}</td>
                    <td>{formatDate(commission.payment_date)}</td>
                    <td>{commission.paid_by_name || commission.paid_by_username || 'N/A'}</td>
                    {canMarkAsPaid && (
                      <td>
                        {commission.payment_status === 'pending' && (
                          commission.self_registered && !commission.ira_licence_uploaded ? (
                            <button
                              type="button"
                              className="btn-mark-paid"
                              disabled
                              title="IRA licence must be on file before this Sign Up commission can be paid."
                            >
                              IRA licence required
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setShowPaymentModal(commission.id);
                                setPaymentData({
                                  paymentDate: new Date().toISOString().split('T')[0],
                                  notes: ''
                                });
                              }}
                              className="btn-mark-paid"
                            >
                              ✅ Mark as Paid
                            </button>
                          )
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
                className="pagination-btn"
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page >= pagination.totalPages}
                className="pagination-btn"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>✅ Mark Commission as Paid</h3>
              <button onClick={() => setShowPaymentModal(null)} className="close-btn">×</button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleMarkAsPaid(showPaymentModal);
              }}
            >
              <div className="form-group">
                <label>Payment Date *</label>
                <input
                  type="date"
                  value={paymentData.paymentDate}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                  required
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  rows={4}
                  placeholder="Add any notes about this payment..."
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">Mark as Paid</button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentModal(null);
                    setPaymentData({ paymentDate: new Date().toISOString().split('T')[0], notes: '' });
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
        />
      )}
    </div>
  );
};

export default CommissionsPayable;

